<i18n>
{
  "en": {
    "createBoostTitle": "Create a product boost",
    "denyCreateBoostTitle": "Can not create product boost",
    "denyCreateBoostMessage": "At this moment can not create new product boost. There is not any completely validated period that prevents to modify prices.",
    "getProductError": "Error getting product information",
    "typeToSearch": "Type to search",
    "emptyFieldError": "Mandatory field",
    "invalidNumberError": "Needs to be a valid number",
    "price": "Price",
    "isSpecial": "Set special status in menu",
    "isRanked": "Set as first of section in menu",
    "isPrice": "Set price",
    "productBoostSubtitle": "Selects a period of time to irreversibly promote the price or position of a product",
    "confirmTitle": "Are you sure you want to create the Product Boost?",
    "confirmText": "Changes will be irreversible during the specified timespan",
    "confirm": "Confirm",
    "lastValidatedPeriodDayError": "Error getting available dates. Please, try it later",
    "selectAProduct": "Select a product",
    "oneOptionRequired": "Select at least one of the following options",
    "dataPickerRequired": "Select a date",
    "selectServices": "Select services"
  },
  "es": {
    "createBoostTitle": "Crea un product boost",
    "denyCreateBoostTitle": "No se puede crear product boost",
    "denyCreateBoostMessage": "En estos momentos no se permite crear nuevos product boost. No hay ningún periodo completamente validado que impida modificar precios.",
    "getProductError": "Error obteniendo información del producto",
    "typeToSearch": "Escribe para buscar",
    "emptyFieldError": "Campo obligatorio",
    "invalidNumberError": "Tiene que ser un número válido",
    "price": "Precio",
    "isSpecial": "Colocar como especial en el menú",
    "isRanked": "Colocar como primero de su sección en el menú",
    "isPrice": "Establecer precio",
    "productBoostSubtitle": "Selecciona un periodo de tiempo para, de manera irreversible, promocionar el precio o posición de un producto",
    "confirmTitle": "¿Está seguro de que quiere crear el Product Boost?",
    "confirmText": "Los cambios serán irreversibles durante el periodo de tiempo establecido",
    "confirm": "Confirmar",
    "lastValidatedPeriodDayError": "Hay un problema cargando el rango de fechas disponibles. Por favor, inténtelo de nuevo más tarde.",
    "selectAProduct": "Elige un producto",
    "oneOptionRequired": "Selecciona al menos una de las siguientes opciones",
    "dataPickerRequired": "Selecciona una fecha",
    "selectServices": "Selecciona los servicios"
  }
}
</i18n>

<template>
  <v-dialog @click:outside="onCancel" v-model="visible" max-width="600px" scrollable persistent>
    <dynameat-confirmation-modal :visible="confirmModalVisible" :title="$t('confirmTitle')"
                                 :description="$t('confirmText')" :confirm-text="$t('confirm')"
                                 @confirm="onConfirmConfirmModal" @cancel="onCloseConfirmModal"/>
    <v-card v-if="visible" height="100%" class="px-7 py-5">
      <div v-if="allowCreateBoost" class="d-flex align-center justify-space-between">
        <v-card-title class="word-break pa-0">{{ $t('createBoostTitle') }}</v-card-title>
      </div>
      <div v-else class="d-flex align-center justify-space-between">
        <v-card-title class="word-break pa-0">{{ $t('denyCreateBoostTitle') }}</v-card-title>
      </div>
      <v-card-subtitle v-if="allowCreateBoost" class="pa-0 mb-4">{{$t('productBoostSubtitle')}}</v-card-subtitle>
      <v-card-subtitle v-else class="pa-0 mb-4">{{ $t('denyCreateBoostMessage') }}</v-card-subtitle>
      <v-container class="pa-0" v-if="allowCreateBoost">
        <v-form v-model="valid" class="d-flex flex-column align-left" ref="form">
          <v-card-subtitle class="pa-0 ma-0 mt-1 mb-1 text-center">{{ $t('dataPickerRequired') + ' *' }}</v-card-subtitle>
          <date-picker class="inline-block h-full mx-auto mb-4" v-model="dates" :min-date="minDate"
                       :max-date="maxDate" trim-weeks is-range ref="datepicker"/>
          <dynameat-autocomplete v-model="selectedProduct" :items="productList" :rules="objectRules"
                                 :filter="onProductTextFilter" :placeholder="$t('typeToSearch')"
                                 :label="$t('selectAProduct')" prepend-icon="mdi-database-search"
                                 item-text="name" chips persistent-hint
                                 return-object :required="true">
            <template v-slot:item="data">
              <v-list-item-avatar>
                <v-img :src="data.item.image" :alt="data.item.name" :lazy-src="defaultProductImg"
                       contain/>
              </v-list-item-avatar>
              <v-list-item-content class="h-scroll">
                <v-list-item-title class="no-wrap">
                  {{ data.item.commercial_name }}
                  (
                  {{currency.hasLeftPosition() ? currency.symbol : ''}}
                  {{ data.item.first_price }}
                  {{currency.hasRightPosition() ? currency.symbol : ''}}
                  )
                </v-list-item-title>
                <v-list-item-subtitle class="no-wrap">{{ data.item.name }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
            <template v-slot:selection="data">
              <v-chip class="caption | d-flex justify-center align-center"
                      close @click:close="onRemoveSelectedProduct">
                <div class="chip-ellipsis">{{ data.item.name }}</div>
              </v-chip>
            </template>
          </dynameat-autocomplete>
          <v-select v-model="services" :items="serviceList" :menu-props="{ maxHeight: '400' }"
          :label="$t('selectServices')" :rules="listRules" item-value="guid"
          item-text="name" chips multiple class="required"/>
          <v-card-subtitle class="pa-0 ma-0 mt-2 text-center">{{ $t('oneOptionRequired') + ' *' }}</v-card-subtitle>
          <v-switch v-model="isPrice" dense :label="$t('isPrice')"/>
          <v-text-field v-if="isPrice" v-model="price" :rules="numberRules" :label="$t('price')"
                        :prefix="currency.hasLeftPosition() ? currency.symbol : ''"
                        :suffix="currency.hasRightPosition() ? currency.symbol : ''"
                        required/>
          <v-switch v-model="isSpecial" dense :label="$t('isSpecial')"/>
          <v-switch v-model="isRanked" dense :label="$t('isRanked')"/>
          <dynameat-modal-buttons @cancel="onCancel" @confirm="onConfirm"
                                  :confirmDisabled="!formValid"
                                  :confirmText="$t('confirm')"/>
        </v-form>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>

import { notEmptyList, notEmptyObject, notValidNumber } from '@core/helpers/formValidators';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import Vue from 'vue';
import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import DatePicker from 'v-calendar/lib/components/date-picker.umd';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import DynameatModalButtons from '@dynameat/components/DynameatModalButtons';
import { createErrorNotification } from '@ulibs/unnotificationsqueue';
import DynameatAutocomplete from '@dynameat/components/DynameatAutocomplete';
import { sentryLogException } from '@core/helpers/sentryLogger';
import { productTextFilter } from '@core/mixins';
import { productBoostRangeRequest } from '../api';
import { ProductBoost, ProductBoostRange } from '../models';

@Component
@Components({
  DatePicker, DynameatConfirmationModal, DynameatModalButtons, DynameatAutocomplete
})
@MapActions(['addNotification'])
@MapGetters(['currency'])
export default class ProductBoostModal extends Vue {
  @Prop({ type: Boolean, default: false }) visible;

  @Prop({ type: Array, default: () => [] }) productList;

  @Prop({ type: Array, default: [] }) serviceList;

  valid = false;

  selectedProduct = null;

  price = null;

  isSpecial = false;

  isRanked = false;

  isPrice = false;

  dates = { start: null, end: null }

  minDate = null;

  maxDate = null;

  confirmModalVisible = false;

  allowCreateBoost = true;

  services = [];

  async created() {
    this.selectedProduct = undefined;
    await this.getProductBoostRange();
    if (!(this.minDate && this.maxDate)) {
      this.allowCreateBoost = false;
    }
  }

  async getProductBoostRange() {
    try {
      const response = await productBoostRangeRequest(this.$route.params.pointOfSaleGuid);
      const productBoostRange = ProductBoostRange.toInstance(response.data);
      this.minDate = productBoostRange.minDate;
      this.maxDate = productBoostRange.maxDate;
    } catch (err) {
      sentryLogException(err, `Error getting last validated period date for POS ${this.$route.params.pointOfSaleGuid}`);
      this.addNotification(createErrorNotification(this.$t('lastValidatedPeriodDayError')));
    }
  }

  get objectRules() {
    return [notEmptyObject(this.$t('emptyFieldError'))];
  }

  get numberRules() {
    if (this.isPrice) {
      return [notValidNumber(this.$t('invalidNumberError'))];
    }
    return [];
  }

  get defaultProductImg() {
    // eslint-disable-next-line global-require
    return require('@dynameat/assets/images/dynameat-logo-secondary.png');
  }

  get validDates() {
    return !!(this.dates.start && this.dates.end);
  }

  get formValid() {
    return this.valid && this.validDates && (this.isPrice || this.isSpecial || this.isRanked);
  }

  get listRules() {
    return [notEmptyList(this.$t('emptyFieldError'))];
  }

  buildProductBoost() {
    return ProductBoost.toInstance({
      guid: null,
      start_date: this.dates.start,
      end_date: this.dates.end,
      price: this.price,
      is_rank_boosted: this.isRanked,
      is_special_boosted: this.isSpecial,
      product: this.selectedProduct.guid,
      services: this.services,
    });
  }

  onConfirm() {
    if (this.formValid) {
      this.onOpenConfirmModal();
    }
  }

  onCancel() {
    this.clearOwnData();
    this.$emit('cancel');
  }

  onProductTextFilter = productTextFilter

  onRemoveSelectedProduct() {
    this.selectedProduct = null;
  }

  clearOwnData() {
    this.dates.start = null;
    this.dates.end = null;
    this.selectedProduct = null;
    this.price = null;
    this.isSpecial = false;
    this.isRanked = false;
    this.isPrice = false;
    this.services = [];
  }

  onOpenConfirmModal() {
    this.confirmModalVisible = true;
  }

  onCloseConfirmModal() {
    this.confirmModalVisible = false;
  }

  async onConfirmConfirmModal() {
    this.confirmModalVisible = false;
    const productBoost = this.buildProductBoost();
    this.clearOwnData();
    this.$emit('confirm', productBoost);
  }
}
</script>
<style scoped>
.progress-enter-active, .progress-leave-active {
  transition: opacity .5s
}

.progress-enter, .progress-leave-to /* .fade-leave-active below version 2.1.8 */
{
  opacity: 0
}

.word-break {
  word-break: break-word;
}

.chip-ellipsis {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.h-scroll {
  overflow-y: hidden;
  overflow-x: auto;
}

.no-wrap {
  text-overflow: initial;
  overflow: initial;
  white-space: nowrap;
}
</style>
